import { type ReactElement, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { config } from '@/config';
import {
  useCreateUploadToken,
  useRevokeUploadToken,
  useTestUploadToken,
  useUploadTokensQuery,
} from '@/features/uploads';
import { useNotifications } from '@/providers/notificationHooks';
import { getTargetActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { useMutationErrorSnackbar } from '@/shared/hooks/useMutationErrorSnackbar';
import {
  Box,
  CodeOutputBlock,
  DialogBody,
  formatDate,
  InfoDialog,
  Panel,
  Stack,
  StatusBanner,
  Text,
} from '@/ui';
import { ButtonGroup } from '@/ui/primitives';
import { CopyIcon, DeleteIcon, Link, RotateRight, Tooltip } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { PlayArrowIcon } from '@/ui/primitives/icons';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { TextField } from '@/ui/primitives/TextField';
import { ProjectSettingsHelperText } from './ProjectSettingsHelperText';
import { ProjectSettingsMetadataRow } from './ProjectSettingsMetadataRow';
import {
  projectUploadSnippetTabSx,
  projectUploadSnippetTabsSx,
  projectUploadSnippetTabsDividerSx,
  projectUploadSnippetContentSx,
  projectSettingsApiKeyBannerSx,
  projectSettingsUploadSnippetBlockSx,
  projectSettingsUploadStatusBannerInnerSx,
  projectSettingsUploadStatusBannerLinkSx,
  projectSettingsUploadStatusBannerSx,
  projectWebhookFieldCellSx,
  projectWebhookFieldInputTextSx,
  projectWebhookFieldRowSx,
  projectWebhookFixedActionButtonSx,
  projectSettingsUploadTokenMetadataPanelSx,
} from './projectSettingsSurfaceSx';
import { resolveTokenStatus } from './uploadTokens/tokenUtils';
import {
  uploadTokenActionButtonSx,
  uploadTokenActionGroupSx,
  uploadTokenActionButtonPrimarySx,
  projectWebhookActionDeleteIconSx as projectUploadActionDeleteIconSx,
  projectWebhookActionPlayIconSx as projectUploadActionPlayIconSx,
  projectWebhookActionRotateIconSx as projectUploadActionRotateIconSx,
} from './uploadTokens/uploadTokenActionSurfaceSx';

type ProjectUploadTokensSectionProps = {
  projectId: string;
  disabled?: boolean;
  disabledReason?: string;
  disabledHref?: string;
};
type ProjectUploadTokensContainer = 'panel' | 'none';
type UploadSnippetFormat = 'XLSX' | 'CSV' | 'XML' | 'JSON';
const maskedUploadTokenValue = '•••••••••••••••••••••••••••••••••••••••••••';

type UploadSnippetBlockProps = {
  format: UploadSnippetFormat;
  value: string;
  onCopy: (snippet: string, format: UploadSnippetFormat) => void;
};

const UploadSnippetBlock = ({ format, value, onCopy }: UploadSnippetBlockProps): ReactElement => (
  <CodeOutputBlock
    value={value}
    onCopy={() => {
      onCopy(value, format);
    }}
    copyAriaLabel={`Copy ${format} upload snippet`}
    background="transparent"
    reserveCopySpace={false}
    sx={projectSettingsUploadSnippetBlockSx}
  />
);

export const ProjectUploadTokenMetadataPanel = ({
  projectId,
}: {
  projectId: string;
}): ReactElement => {
  const tokensQuery = useUploadTokensQuery(projectId);
  const tokens = tokensQuery.data ?? [];
  const activeTokens = tokens.filter((token) => resolveTokenStatus(token) === 'Active');
  const latestActiveToken = activeTokens.length > 0 ? activeTokens[0] : null;
  const createdLabel =
    formatDate(latestActiveToken?.createdAt ?? null, { includeTime: true }) || '-';
  const lastUsedLabel =
    formatDate(latestActiveToken?.lastUsedAt ?? null, { includeTime: true }) || '-';

  return (
    <Panel surfaceVariant="settings" sx={projectSettingsUploadTokenMetadataPanelSx}>
      <ProjectSettingsMetadataRow
        items={[
          { label: 'Created', value: createdLabel },
          { label: 'Last Used', value: lastUsedLabel },
        ]}
      />
    </Panel>
  );
};

export const ProjectUploadTokensSection = ({
  projectId,
  disabled = false,
  disabledReason = 'Upload token management is not available on Free plans.',
  disabledHref = '/plan-settings',
  container = 'panel',
  initialSnippetTab = 'XLSX',
}: ProjectUploadTokensSectionProps & {
  container?: ProjectUploadTokensContainer;
  initialSnippetTab?: UploadSnippetFormat;
}): ReactElement => {
  const { notify } = useNotifications();
  const mutationError = useMutationErrorSnackbar();
  const tokensQuery = useUploadTokensQuery(projectId);
  const createToken = useCreateUploadToken();
  const revokeToken = useRevokeUploadToken();
  const testToken = useTestUploadToken();
  const [tokenValue, setTokenValue] = useState('');
  const [activeSnippetTab, setActiveSnippetTab] = useState<UploadSnippetFormat>(initialSnippetTab);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [snippetsOpen, setSnippetsOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const uploadEndpoint = `${config.apiBaseUrl.replace(/\/+$/, '')}/uploads`;
  const tokens = tokensQuery.data ?? [];
  const activeTokens = useMemo(
    () => tokens.filter((token) => resolveTokenStatus(token) === 'Active'),
    [tokens],
  );
  const hasPlaintextToken = tokenValue.trim().length > 0;
  const hasConfiguredToken = hasPlaintextToken || activeTokens.length > 0;
  const displayTokenValue = hasPlaintextToken
    ? tokenValue
    : hasConfiguredToken
      ? maskedUploadTokenValue
      : '';
  const savePending = createToken.isPending || revokeToken.isPending;
  const testPending = testToken.isPending;
  const disabledActionLabel = disabled ? disabledReason : null;
  const uploadXmlSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/xml" \\',
    '  --header "x-file-name: $XML_FILE_NAME" \\',
    '  --data-binary @"$XML_FILE_PATH"',
  ].join('\n');
  const uploadJsonSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/json" \\',
    '  --header "x-file-name: $JSON_FILE_NAME" \\',
    '  --data-binary @"$JSON_FILE_PATH"',
  ].join('\n');
  const uploadCsvSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: text/csv" \\',
    '  --header "x-file-name: $CSV_FILE_NAME" \\',
    '  --data-binary @"$CSV_FILE_PATH"',
  ].join('\n');
  const uploadXlsxSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" \\',
    '  --header "x-file-name: $XLSX_FILE_NAME" \\',
    '  --data-binary @"$XLSX_FILE_PATH"',
  ].join('\n');

  const copyUploadToken = (): void => {
    if (!hasPlaintextToken) return;
    void navigator.clipboard.writeText(tokenValue);
    notify({
      message: getToastMessage('clipboard.copied', { label: 'Upload token' }),
      tone: 'green',
    });
  };

  const copyUploadSnippet = (snippet: string, format: UploadSnippetFormat): void => {
    void navigator.clipboard.writeText(snippet);
    notify({
      message: getToastMessage('upload.snippetCopied', { format }),
      tone: 'green',
    });
  };

  const applyGenerateToken = async (): Promise<void> => {
    if (disabled) return;
    try {
      for (const token of activeTokens) {
        await revokeToken.mutateAsync({ projectId, tokenId: token.id });
      }
      const created = await createToken.mutateAsync({
        projectId,
        input: { name: `ci-upload-token-${Date.now()}` },
      });
      setTokenValue(created.token);
      setBannerDismissed(false);
      mutationError.clearError();
      setRegenerateOpen(false);
      notify({
        message: hasConfiguredToken ? 'Regenerated upload token' : 'Generated upload token',
        tone: 'green',
      });
    } catch (err) {
      mutationError.setError(err);
    }
  };

  const onGenerateOrRegenerate = (): void => {
    if (disabled) return;
    if (hasConfiguredToken) {
      setRegenerateOpen(true);
      return;
    }
    void applyGenerateToken();
  };

  const applyRevoke = async (): Promise<void> => {
    if (disabled) return;
    try {
      for (const token of activeTokens) {
        await revokeToken.mutateAsync({ projectId, tokenId: token.id });
      }
      setTokenValue('');
      setRevokeOpen(false);
      mutationError.clearError();
      notify({
        message: getToastMessage('entity.revoked', { entity: 'upload token' }),
        tone: 'green',
      });
    } catch (err) {
      mutationError.setError(err);
    }
  };

  const onTestUpload = (): void => {
    if (disabled) return;
    if (!hasPlaintextToken) return;
    testToken.mutate(
      { projectId, input: { token: tokenValue } },
      {
        onSuccess: () => {
          mutationError.clearError();
          notify({ message: getToastMessage('uploadToken.testPassed'), tone: 'green' });
        },
        onError: (err) => {
          mutationError.setError(err);
        },
      },
    );
  };

  const content = (
    <Stack gap="md">
      {disabledActionLabel ? (
        <StatusBanner
          tone="orange"
          variant="outlined"
          dense
          sx={projectSettingsUploadStatusBannerSx}
        >
          <Box sx={projectSettingsUploadStatusBannerInnerSx}>
            <Text role="body" component="span">
              {disabledActionLabel}
            </Text>
            {disabledHref ? (
              <Link
                href={disabledHref}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={projectSettingsUploadStatusBannerLinkSx}
              >
                Upgrade
              </Link>
            ) : null}
          </Box>
        </StatusBanner>
      ) : null}
      <Stack gap="lg">
        <Stack gap="md">
          <Box sx={projectWebhookFieldRowSx}>
            <Box sx={projectWebhookFieldCellSx}>
              <TextField
                fieldStyle="settings"
                value={displayTokenValue}
                placeholder="Generate an upload token"
                slotProps={{ input: { readOnly: true } }}
                sx={projectWebhookFieldInputTextSx}
                onFocus={(event) => {
                  event.target.select();
                }}
              />
            </Box>
            <ButtonGroup
              variant="outlined"
              aria-label="Upload token actions"
              sx={uploadTokenActionGroupSx}
            >
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                loading={!hasConfiguredToken && savePending}
                aria-label={
                  hasConfiguredToken ? 'Regenerate Upload Token' : 'Generate Upload Token'
                }
                disabled={disabled || savePending}
                sx={
                  !hasConfiguredToken
                    ? [uploadTokenActionButtonSx, uploadTokenActionButtonPrimarySx]
                    : [uploadTokenActionButtonSx]
                }
                onClick={onGenerateOrRegenerate}
              >
                <Tooltip
                  title={hasConfiguredToken ? 'Regenerate Upload Token' : 'Generate Upload Token'}
                  placement="top"
                >
                  <RotateRight sx={projectUploadActionRotateIconSx} />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                loading={testPending}
                aria-label="Test Upload Token"
                disabled={disabled || !hasPlaintextToken || savePending || testPending}
                sx={[uploadTokenActionButtonSx, projectWebhookFixedActionButtonSx]}
                onClick={onTestUpload}
              >
                <Tooltip title="Test Upload Token" placement="top">
                  <PlayArrowIcon sx={projectUploadActionPlayIconSx} />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                aria-label={getTargetActionLabel('copy', 'Upload Token')}
                disabled={disabled || !hasPlaintextToken || savePending}
                sx={uploadTokenActionButtonSx}
                onClick={copyUploadToken}
              >
                <Tooltip title={getTargetActionLabel('copy', 'Upload Token')} placement="top">
                  <CopyIcon />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                aria-label={getTargetActionLabel('delete', 'Upload Token')}
                disabled={disabled || !hasConfiguredToken || savePending}
                sx={uploadTokenActionButtonSx}
                onClick={() => {
                  setRevokeOpen(true);
                }}
              >
                <Tooltip title={getTargetActionLabel('delete', 'Upload Token')} placement="top">
                  <DeleteIcon sx={projectUploadActionDeleteIconSx} />
                </Tooltip>
              </Button>
            </ButtonGroup>
          </Box>
          {hasPlaintextToken && !bannerDismissed ? (
            <StatusBanner
              tone="blue"
              variant="outlined"
              message="Copy the token now. It is only shown once."
              onClose={() => {
                setBannerDismissed(true);
              }}
              sx={projectSettingsApiKeyBannerSx}
            />
          ) : null}
          {hasConfiguredToken ? (
            <ProjectSettingsMetadataRow
              items={[
                {
                  label: 'Created',
                  value:
                    formatDate(activeTokens[0]?.createdAt ?? null, { includeTime: true }) || '-',
                },
                {
                  label: 'Last Used',
                  value:
                    formatDate(activeTokens[0]?.lastUsedAt ?? null, { includeTime: true }) || '-',
                },
              ]}
              actionSlot={
                <Button
                  variant="secondary"
                  fullWidthOnMobile={false}
                  onClick={() => {
                    setSnippetsOpen(true);
                  }}
                >
                  View Snippets
                </Button>
              }
            />
          ) : null}
        </Stack>
      </Stack>
      <InfoDialog
        open={snippetsOpen}
        onClose={() => {
          setSnippetsOpen(false);
        }}
        title="Upload Snippets"
      >
        <DialogBody gap="md">
          <ProjectSettingsHelperText>
            Use these snippets with your generated token to upload results files via cURL.
          </ProjectSettingsHelperText>
          <Box sx={projectUploadSnippetTabsDividerSx}>
            <Tabs
              value={activeSnippetTab}
              onChange={(_, next) => {
                setActiveSnippetTab(next as UploadSnippetFormat);
              }}
              aria-label="Upload snippet format tabs"
              sx={projectUploadSnippetTabsSx}
            >
              <Tab label="XLSX" value="XLSX" sx={projectUploadSnippetTabSx} />
              <Tab label="CSV" value="CSV" sx={projectUploadSnippetTabSx} />
              <Tab label="XML" value="XML" sx={projectUploadSnippetTabSx} />
              <Tab label="JSON" value="JSON" sx={projectUploadSnippetTabSx} />
            </Tabs>
          </Box>
          <Box sx={projectUploadSnippetContentSx}>
            {activeSnippetTab === 'XLSX' ? (
              <UploadSnippetBlock
                format="XLSX"
                value={uploadXlsxSnippet}
                onCopy={copyUploadSnippet}
              />
            ) : null}
            {activeSnippetTab === 'CSV' ? (
              <UploadSnippetBlock
                format="CSV"
                value={uploadCsvSnippet}
                onCopy={copyUploadSnippet}
              />
            ) : null}
            {activeSnippetTab === 'XML' ? (
              <UploadSnippetBlock
                format="XML"
                value={uploadXmlSnippet}
                onCopy={copyUploadSnippet}
              />
            ) : null}
            {activeSnippetTab === 'JSON' ? (
              <UploadSnippetBlock
                format="JSON"
                value={uploadJsonSnippet}
                onCopy={copyUploadSnippet}
              />
            ) : null}
          </Box>
        </DialogBody>
      </InfoDialog>
      <ConfirmDialog
        open={regenerateOpen}
        title="Regenerate Upload Token"
        description="Generate a new upload token for this project."
        confirmLabel="Regenerate Upload Token"
        confirmColor="primary"
        confirmVariant="contained"
        loading={savePending}
        onClose={() => {
          setRegenerateOpen(false);
        }}
        onConfirm={() => {
          void applyGenerateToken();
        }}
      >
        <DialogBody>
          <ProjectSettingsHelperText>
            This action revokes the existing upload token immediately.
          </ProjectSettingsHelperText>
          <ProjectSettingsHelperText>
            Update your upload token after regenerating to avoid upload failures.
          </ProjectSettingsHelperText>
        </DialogBody>
      </ConfirmDialog>
      <ConfirmDialog
        open={revokeOpen}
        title={getDialogMessage('destructive.deleteNamed.title', { label: 'Upload Token' })}
        description="Delete this upload token to immediately block CI uploads using it."
        confirmLabel={getDialogMessage('destructive.deleteNamed.confirmLabel', {
          label: 'Upload Token',
        })}
        confirmColor="error"
        loading={savePending}
        onClose={() => {
          setRevokeOpen(false);
        }}
        onConfirm={() => {
          void applyRevoke();
        }}
      >
        <DialogBody>
          <ProjectSettingsHelperText>
            {getDialogMessage('destructive.cannotBeUndone')}
          </ProjectSettingsHelperText>
        </DialogBody>
      </ConfirmDialog>
    </Stack>
  );
  if (container === 'none') {
    return content;
  }
  return (
    <Panel title="Upload Token" padding="xl">
      {content}
    </Panel>
  );
};
